import { useState } from 'react';
import type { DialogProps } from '@mui/material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import type { Editor } from '@tiptap/core';

import { Or2Button } from 'Components/button/Or2Button';

import type { RootOwner } from './file-selector/FileSelectorDialog';
import { FileSelectorButton } from './file-selector/FileSelectorButton';

type Props = DialogProps & {
  editor: Editor | null;
  rootOwner?: RootOwner;
  disableFileSelector: boolean;
  open: boolean;
  onClose: () => void;
};

export const AnchorDialog = ({
  rootOwner,
  editor,
  disableFileSelector,
  open,
  onClose,
  ...other
}: Props) => {
  const [displayText, setDisplayText] = useState('');
  const [url, setUrl] = useState('');
  const [parentId, setParentId] = useState<string | null | undefined>(null);

  return (
    <Dialog
      slotProps={{
        transition: {
          onEnter: () => {
            if (!editor) {
              return;
            }

            editor.chain().focus().extendMarkRange('link').run();

            const { view, state } = editor;
            const { from, to } = view.state.selection;
            const selectedText = state.doc.textBetween(from, to, '');

            setDisplayText(selectedText);
            setUrl(editor.getAttributes('link')?.href || '');
          },
        },
      }}
      open={open}
      onClose={onClose}
      {...other}
    >
      <DialogTitle>Edit link</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Text to display"
            value={displayText}
            onChange={(event) => setDisplayText(event.target.value)}
          />
          {disableFileSelector || !rootOwner ? (
            <TextField
              fullWidth
              label="Url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
          ) : (
            <Stack spacing={2} direction="row">
              <TextField
                fullWidth
                label="Url"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
              />
              <FileSelectorButton
                rootOwner={rootOwner}
                displayText={displayText}
                parentId={parentId}
                setDisplayText={setDisplayText}
                setUrl={setUrl}
                setParentId={setParentId}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Or2Button color="secondary" onClick={onClose}>
          Cancel
        </Or2Button>
        <Or2Button
          onClick={() => {
            if (!editor) {
              return;
            }

            if (!url) {
              editor.chain().focus().extendMarkRange('link').unsetLink().run();

              return;
            }

            editor
              .chain()
              .focus()
              .extendMarkRange('link')
              .setLink({ href: url })
              .insertContent(`<p>${displayText}</p>`)
              .run();

            onClose();
          }}
        >
          OK
        </Or2Button>
      </DialogActions>
    </Dialog>
  );
};
